import React, { useEffect, useState, useRef } from 'react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import BackButton from '../components/BackButton';
import organization from '../content/organization.json';
import { Users, GraduationCap, Shield, CheckCircle, Building2, TreePine, Factory, Briefcase, Heart, Target, Globe, Hammer, TreeDeciduous, ShoppingCart, Home, Warehouse, Car, Plane, Flower2, ChevronRight, ArrowRight, Sparkles, Zap, Network } from 'lucide-react';

import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

// Animation hook for intersection observer
const useInView = (threshold = 0.1) => {
  const [isInView, setIsInView] = useState(true);
  const ref = useRef(null);

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
        }
      },
      { threshold }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [threshold]);

  return [ref, isInView];
};

export default function QuiSommesNous() {
  const [heroRef, heroInView] = useInView();
  const [missionRef, missionInView] = useInView();
  const [govRef, govInView] = useInView();
  const [ecoRef, ecoInView] = useInView();

  const stats = [
    { value: 12, label: 'Branches prévues aux statuts', icon: Building2 },
    { value: 'SCIC', label: 'Société coopérative', icon: Users },
    { value: 'ESS', label: 'Un projet d’intérêt collectif', icon: Heart },
    { value: organization.governanceColleges.length, label: 'Collèges de gouvernance', icon: Network }
  ];

  const branches = [
    { name: 'BATI\'SCOP', desc: 'Construction neuve et Réhabilitation', icon: Hammer, color: 'from-amber-500 to-orange-600' },
    { name: 'O\'SCOP Industrie bois', desc: 'Produits finis pour l\'habitat', icon: TreeDeciduous, color: 'from-lime-500 to-green-600' },
    { name: 'BRICO\'SCOP', desc: 'Plateforme OMNICANAL', icon: ShoppingCart, color: 'from-pink-500 to-rose-600' },
    { name: 'O\'SCOP GE', desc: 'Groupement d\'Employeurs', icon: Users, color: 'from-purple-500 to-indigo-600' },
    { name: 'FORMA\'SCOP', desc: 'Centre de formation', icon: GraduationCap, color: 'from-blue-500 to-cyan-600' },
    { name: 'IMMO\'SCOP', desc: 'Habitat solidaire', icon: Home, color: 'from-indigo-500 to-purple-600' },
    { name: 'LOGI\'SCOP', desc: 'Logistique', icon: Warehouse, color: 'from-orange-500 to-red-600' },
    { name: 'O\'SCOP Mobilité', desc: 'Transport de personnes', icon: Car, color: 'from-cyan-500 to-blue-600' },
    { name: 'O\'SCOP Airways', desc: 'Mobilité aérienne', icon: Plane, color: 'from-sky-500 to-blue-600' },
    { name: 'HORTI\'SCOP', desc: 'Horticulture', icon: TreePine, color: 'from-teal-500 to-emerald-600' },
    { name: 'FLORAL\'SCOP', desc: 'Fleuristerie', icon: Flower2, color: 'from-rose-500 to-pink-600' },
    { name: 'AGRO\'SCOP', desc: 'Agro-transformation', icon: Factory, color: 'from-emerald-500 to-green-600' },
  ];

  return (
    <div className="min-h-screen bg-slate-950">
      <Navigation />
      
      {/* Animated Hero Section */}
      <section 
        ref={heroRef}
        className="relative min-h-[90vh] flex items-center overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950"
      >
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1600880292089-90a7e086ee0c?w=1920&q=80" 
            alt="Teamwork collaboration" 
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/80 via-slate-900/90 to-slate-950" />
        </div>
        
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 -left-20 w-[600px] h-[600px] bg-gradient-to-r from-blue-600/20 to-purple-600/20 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-1/4 -right-20 w-[500px] h-[500px] bg-gradient-to-r from-amber-500/20 to-orange-500/20 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}} />
          
          {/* Animated grid pattern */}
          <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:60px_60px]" />
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 py-20">
          <BackButton to="/" label="Retour à l'accueil" className="text-white/70 mb-8 hover:text-white transition-colors" />
          
          <div className={`transition-all duration-1000 ${heroInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <div className="flex items-center gap-3 mb-6">
              <div className="flex items-center gap-2 bg-gradient-to-r from-amber-500/20 to-orange-500/20 backdrop-blur-xl px-4 py-2 rounded-full border border-amber-500/30">
                <Sparkles className="w-4 h-4 text-amber-400" />
                <span className="text-sm font-medium text-amber-300">Société Coopérative d'Intérêt Collectif</span>
              </div>
            </div>
            
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-black mb-6 leading-tight">
              <span className="bg-gradient-to-r from-white via-blue-100 to-white bg-clip-text text-transparent">
                OBJECTIF SCOP
              </span>
              <br />
              <span className="bg-gradient-to-r from-amber-400 via-orange-400 to-amber-400 bg-clip-text text-transparent">
                OUTREMER
              </span>
            </h1>
            
            <p className="text-xl md:text-2xl text-slate-300 max-w-3xl mb-8 leading-relaxed">
              {organization.legalName}, connue sous le nom {organization.name}, est une SCIC SAS à capital variable.
              Elle organise des services mutualisés et des projets d’intérêt collectif au service des{' '}
              <span className="text-amber-400 font-semibold">territoires ultramarins</span>.
            </p>

            <div className="flex flex-wrap gap-4 mb-12">
              <Button 
                size="lg" 
                className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-bold px-8 py-6 text-lg shadow-2xl shadow-amber-500/25 hover:shadow-amber-500/40 transition-all duration-300 hover:scale-105"
                onClick={() => window.location.href = '/adhesion'}
              >
                Rejoindre O'SCOP
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                className="border-2 border-white/20 text-white hover:bg-white/10 backdrop-blur-sm px-8 py-6 text-lg transition-all duration-300"
                onClick={() => window.location.href = '/services'}
              >
                Découvrir nos services
              </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {stats.map((stat, index) => (
                <div 
                  key={index}
                  className="bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10 hover:border-amber-500/50 transition-all duration-300 hover:bg-white/10 group"
                >
                  <stat.icon className="w-8 h-8 text-amber-400 mb-3 group-hover:scale-110 transition-transform" />
                  <div className="text-3xl md:text-4xl font-black text-white mb-1">
                    {stat.value}
                  </div>
                  <div className="text-sm text-slate-400">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-8 h-12 rounded-full border-2 border-white/30 flex items-start justify-center p-2">
            <div className="w-1 h-3 bg-white/50 rounded-full animate-pulse" />
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section 
        ref={missionRef}
        className="py-24 bg-gradient-to-b from-slate-950 to-slate-900 relative overflow-hidden"
      >
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(59,130,246,0.1),transparent_50%)]" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className={`transition-all duration-1000 delay-200 ${missionInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <div className="flex items-center gap-3 mb-4">
              <div className="h-px w-12 bg-gradient-to-r from-amber-500 to-transparent" />
              <span className="text-amber-400 font-semibold uppercase tracking-wider text-sm">Notre raison d'être</span>
            </div>
            
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-12">
              Construire l'avenir des <span className="text-amber-400">Outre-mer</span>
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                { icon: Users, color: 'blue', title: 'Accompagner les entreprises', desc: 'Outils RH, formation, structuration et soutien opérationnel pour les professionnels ultramarins.' },
                { icon: Globe, color: 'green', title: 'Soutenir les dynamiques territoriales', desc: 'Création de pôles d\'activité adaptés aux besoins réels des Outre-mer.' },
                { icon: Briefcase, color: 'amber', title: 'Développer l\'emploi durable', desc: 'Grâce au Groupement d\'Employeurs O\'SCOP GE et à FORMA\'SCOP.' },
                { icon: Network, color: 'purple', title: 'Construire un modèle coopératif', desc: 'Inclusif et résilient pour faire émerger de nouvelles opportunités.' }
              ].map((item, index) => {
                const colors = {
                  blue: 'from-blue-500 to-cyan-500 border-blue-500/30',
                  green: 'from-green-500 to-emerald-500 border-green-500/30',
                  amber: 'from-amber-500 to-orange-500 border-amber-500/30',
                  purple: 'from-purple-500 to-pink-500 border-purple-500/30'
                };
                return (
                  <div 
                    key={index}
                    className={`group relative bg-slate-800/50 backdrop-blur-xl rounded-2xl p-8 border border-slate-700/50 hover:border-slate-600 transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl hover:shadow-${item.color}-500/10`}
                    style={{ transitionDelay: `${index * 100}ms` }}
                  >
                    <div className={`absolute inset-0 bg-gradient-to-br ${colors[item.color]} opacity-0 group-hover:opacity-5 rounded-2xl transition-opacity duration-500`} />
                    <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${colors[item.color]} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                      <item.icon className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-3">{item.title}</h3>
                    <p className="text-slate-400 leading-relaxed">{item.desc}</p>
                    <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
                      <ChevronRight className="w-5 h-5 text-slate-500" />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Governance Section */}
      <section 
        ref={govRef}
        className="py-24 bg-slate-900 relative overflow-hidden"
      >
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-gradient-to-b from-purple-600/10 to-transparent rounded-full blur-3xl" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className={`transition-all duration-1000 ${govInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <div className="text-center mb-16">
              <Badge className="bg-purple-500/20 text-purple-300 border border-purple-500/30 mb-4 px-4 py-2">
                <Network className="w-4 h-4 mr-2" />
                Gouvernance démocratique
              </Badge>
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                7 collèges pour une <span className="text-purple-400">décision partagée</span>
              </h2>
              <p className="text-xl text-slate-400 max-w-2xl mx-auto">
                Une personne égale une voix à l’intérieur de chaque collège. Le résultat de chaque collège
                est ensuite affecté de sa pondération statutaire.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-6">
              {organization.governanceColleges.map((college, index) => (
                <div 
                  key={college.code}
                  className="group bg-slate-800/50 backdrop-blur-xl rounded-2xl p-4 border border-slate-700/50 hover:border-slate-600 transition-all duration-300 hover:-translate-y-1 text-center"
                >
                  <div className={`w-4 h-4 ${['bg-blue-500', 'bg-amber-500', 'bg-green-500', 'bg-orange-500', 'bg-purple-500', 'bg-cyan-500', 'bg-rose-500'][index]} rounded-full mx-auto mb-3 group-hover:scale-125 transition-transform shadow-lg`} />
                  <p className="text-sm text-slate-300 font-medium">{college.code} — {college.label}</p>
                  <p className="text-2xl font-bold text-purple-300 mt-3">{college.weight} %</p>
                </div>
              ))}
            </div>
            <p className="text-sm text-slate-400 text-center mb-12">
              {organization.statutoryReference.title}, articles 22 à 25. Les modalités prévues pour les
              collèges temporairement vides s’appliquent selon les statuts.
            </p>

            <div className="bg-gradient-to-r from-slate-800/80 to-slate-800/40 backdrop-blur-xl rounded-3xl p-8 border border-slate-700/50">
              <h3 className="text-xl font-semibold mb-6 text-center text-white">Nos décisions reposent sur</h3>
              <div className="flex flex-wrap justify-center gap-3">
                {['Participation', 'Transparence', 'Partage de valeur', 'Solidarité', 'Intérêt général'].map((value, index) => (
                  <span 
                    key={index} 
                    className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-500/10 to-orange-500/10 text-amber-300 px-5 py-3 rounded-full font-medium border border-amber-500/20 hover:border-amber-500/50 transition-colors cursor-default"
                  >
                    <CheckCircle className="w-4 h-4" />
                    {value}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Ecosystem Section - Branches */}
      <section 
        ref={ecoRef}
        className="py-24 bg-gradient-to-b from-slate-900 to-slate-950 relative overflow-hidden"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className={`transition-all duration-1000 ${ecoInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <div className="text-center mb-16">
              <Badge className="bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 mb-4 px-4 py-2">
                <Building2 className="w-4 h-4 mr-2" />
                Écosystème coopératif
              </Badge>
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                12 branches pour une <span className="text-emerald-400">économie locale forte</span>
              </h2>
              <p className="text-lg text-slate-400 max-w-3xl mx-auto">
                Ces branches sont prévues par les statuts. Contactez-nous pour connaître les prestations
                accessibles, leurs conditions et les modalités d’intervention sur votre territoire.
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {branches.map((branch, index) => (
                <div 
                  key={index}
                  className="group bg-slate-800/30 backdrop-blur-xl rounded-2xl p-5 border border-slate-700/50 hover:border-slate-600 transition-all duration-500 hover:-translate-y-2 hover:shadow-xl cursor-pointer"
                  style={{ transitionDelay: `${index * 50}ms` }}
                >
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${branch.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-lg`}>
                    <branch.icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-bold text-white mb-1 group-hover:text-amber-400 transition-colors">{branch.name}</h3>
                  <p className="text-sm text-slate-500">{branch.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ESS Commitments */}
      <section className="py-24 bg-gradient-to-br from-amber-600 via-orange-600 to-amber-700 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[linear-gradient(rgba(0,0,0,0.1)_1px,transparent_1px),linear-gradient(90deg,rgba(0,0,0,0.1)_1px,transparent_1px)] bg-[size:40px_40px]" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Nos engagements <span className="text-amber-200">ESS</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Heart, title: 'Solidarité coopérative', desc: 'Chaque action sert l\'intérêt collectif.' },
              { icon: Target, title: 'Mutualisation des ressources', desc: 'Transformer les contraintes des TPE en leviers de croissance.' },
              { icon: GraduationCap, title: 'Développement des compétences', desc: 'FORMA\'SCOP forme les professionnels de demain.' },
              { icon: Shield, title: 'Sécurisation économique', desc: 'O\'SCOP GE protège les entreprises et les salariés.' },
              { icon: Globe, title: 'Impact territorial', desc: 'Contribuer à la résilience et l\'autonomie des Outre-mer.' },
              { icon: Zap, title: 'Innovation sociale', desc: 'Créer de nouveaux modèles pour une économie plus juste.' }
            ].map((item, index) => (
              <div 
                key={index}
                className="bg-white/10 backdrop-blur-xl rounded-2xl p-8 border border-white/20 hover:bg-white/20 transition-all duration-300 hover:-translate-y-2 group"
              >
                <div className="w-16 h-16 rounded-2xl bg-white/20 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <item.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{item.title}</h3>
                <p className="text-amber-100/80">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-slate-950 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[1000px] h-[500px] bg-gradient-to-t from-blue-600/20 to-transparent rounded-full blur-3xl" />
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Prêt à rejoindre <span className="text-amber-400">O'SCOP</span> ?
          </h2>
          <p className="text-xl text-slate-400 mb-10">
            Entreprises, travailleurs, partenaires : construisons ensemble un modèle innovant pour les territoires ultramarins.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-bold px-10 py-6 text-lg shadow-2xl shadow-amber-500/25 hover:shadow-amber-500/40 transition-all duration-300 hover:scale-105"
              onClick={() => window.location.href = '/adhesion'}
            >
              Adhérer maintenant
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <Button 
              size="lg" 
              variant="outline"
              className="border-2 border-slate-600 text-white hover:bg-slate-800 px-10 py-6 text-lg transition-all duration-300"
              onClick={() => window.location.href = '/contact'}
            >
              Nous contacter
            </Button>
          </div>
        </div>
      </section>

      <Footer />

      {/* Global styles for animations */}
      <style jsx global>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); opacity: 0.3; }
          50% { transform: translateY(-20px); opacity: 0.8; }
        }
        .animate-float {
          animation: float 4s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}
